import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { CORE_ROUTE, CoreState, CustomerManagementSelector, Jurisdiction } from "@obg/ngx.core";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { RouteAction } from "@obg/ngx.router";
import { merge, Subscription } from "rxjs";
import { filter, map, tap } from "rxjs/operators";

import { UpdatedTermsAndConditionsConfigService } from "./updated-terms-and-conditions.config";
import { UpdatedTermsAndConditionsService } from "./updated-terms-and-conditions.service";

@Component({
	selector: "obg-m-updated-terms-and-conditions-container",
	templateUrl: "./updated-terms-and-conditions.container.html",
	styleUrls: ["./updated-terms-and-conditions.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class UpdatedTermsAndConditionsContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-updated-terms-and-conditions") hostCssClass = true;
	@ViewChild("content", { static: false }) content!: ElementRef;

	@Input()
	@coerceBoolean()
	hideReadMore = false;

	@Input()
	@coerceBoolean()
	allLinksExternal = false;

	@Output() close = new EventEmitter<void>();
	@Output() busy = new EventEmitter<boolean>();

	errorMessage = "";
	messageKey = "";
	summaryKey = "";
	readMoreLinkRoute = CORE_ROUTE.termsAndConditions;
	isExtendedUi = this.config.isExtended();
	isBusy = false;
	showDeclinedWarning: boolean | undefined;
	declineFormGroup = this.updatedTermsAndConditionsService.declineFormGroup;

	private termsAndConditionsToken$$: Subscription | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private changeDetector: ChangeDetectorRef,
		private config: UpdatedTermsAndConditionsConfigService,
		private updatedTermsAndConditionsService: UpdatedTermsAndConditionsService
	) {
		this.updatedTermsAndConditionsService.initialize();
	}

	ngOnInit() {
		const error$ = this.updatedTermsAndConditionsService.errorMessage$.pipe(
			tap((errorMessage) => (this.errorMessage = errorMessage))
		);

		const isBusy$ = this.updatedTermsAndConditionsService.isBusy$.pipe(
			tap((isBusy) => {
				this.isBusy = isBusy;
				this.busy.emit(isBusy);
			})
		);

		const keys$ = this.store.select(CustomerManagementSelector.getTermsAndConditionsJurisdiction).pipe(
			filter((value) => !!value),
			map((value) => (value === Jurisdiction.Sga ? "sga-" : "")),
			tap(
				(prefix) =>
					([this.messageKey, this.summaryKey] = [
						`${prefix}terms-and-conditions.updated-content`,
						`${prefix}terms-and-conditions.summary`,
					])
			)
		);

		const isAtDeclineWarningState$ = this.updatedTermsAndConditionsService.isDeclineWarningState$.pipe(
			tap((isDeclineWarningState) => {
				this.showDeclinedWarning = isDeclineWarningState;
				this.busy.emit(false);
			})
		);

		const hasDeclined$ = this.store.select(CustomerManagementSelector.hasDeclinedLatestTermsAndConditions).pipe(
			filter((isDeclined) => !!isDeclined),
			tap(() => {
				this.updatedTermsAndConditionsService.declinedTermsAndConditionsSnackbar();
				this.updatedTermsAndConditionsService.setDeclineWarningState(false);
			})
		);

		this.data$$ = merge(error$, isBusy$, keys$, isAtDeclineWarningState$, hasDeclined$).subscribe(() =>
			this.changeDetector.markForCheck()
		);
	}

	ngOnDestroy() {
		if (this.termsAndConditionsToken$$) {
			this.termsAndConditionsToken$$.unsubscribe();
		}

		this.data$$?.unsubscribe();
	}

	onAccept() {
		if (this.isBusy) {
			return;
		}
		this.updatedTermsAndConditionsService.acceptTermsAndConditions();
	}

	onDecline() {
		this.updatedTermsAndConditionsService.setDeclineWarningState(true);
		this.store.dispatch(RouteAction.navigate(this.readMoreLinkRoute));
		this.close.emit();
		this.content.nativeElement.scrollIntoView();
	}

	onConfirmDecline() {
		this.updatedTermsAndConditionsService.declineTermsAndConditions();
	}

	onCancelDecline() {
		this.updatedTermsAndConditionsService.resetFormState();
		this.updatedTermsAndConditionsService.setDeclineWarningState(false);
	}

	onReadMore() {
		this.close.emit();
	}
}
